import React, { useMemo } from "react";
import { Check } from "lucide-react";
import { cn } from "../../utils/cn";
import { PASSWORD_RULES } from "../../utils/authSchemas";

const STRENGTH = [
  { label: "Weak", bar: "bg-destructive", text: "text-destructive", width: "w-1/4" },
  { label: "Fair", bar: "bg-orange-400", text: "text-orange-500", width: "w-2/4" },
  { label: "Good", bar: "bg-yellow-400", text: "text-yellow-600", width: "w-3/4" },
  { label: "Strong", bar: "bg-success", text: "text-success", width: "w-full" },
];

const PasswordStrength = ({ password }: { password: string }) => {
  const checks = useMemo(
    () =>
      PASSWORD_RULES.map((rule) => ({
        label: rule.label,
        met: rule.test(password),
      })),
    [password]
  );

  if (!password) return null;

  const met = checks.filter((check) => check.met).length;
  const strength =
    met <= 2 ? STRENGTH[0] : met === 3 ? STRENGTH[1] : met === 4 ? STRENGTH[2] : STRENGTH[3];

  return (
    <div className="mt-3 rounded-xl border bg-muted/50 p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[12px] font-medium text-muted-foreground">
          Password strength
        </p>
        <p className={cn("text-[12px] font-semibold", strength.text)}>
          {strength.label}
        </p>
      </div>

      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border">
        <div
          className={cn(
            "h-full rounded-full transition-all duration-300",
            strength.bar,
            strength.width
          )}
        />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 xs:grid-cols-2">
        {checks.map((check) => (
          <div key={check.label} className="flex items-center gap-2 text-[12px]">
            <span
              className={cn(
                "flex size-4 shrink-0 items-center justify-center rounded-full transition-colors",
                check.met
                  ? "bg-success text-success-foreground"
                  : "bg-border text-transparent"
              )}
            >
              <Check className="size-2.5" strokeWidth={3} />
            </span>
            <span className={check.met ? "text-foreground" : "text-muted-foreground"}>
              {check.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrength;
